/**
 * Convite por e-mail: a pessoa cria a senha pelo link que recebeu.
 *
 * Mesma moldura e mesmas peças do primeiro acesso (CamposDaSenha), sem a
 * senha provisória — no convite não existe senha anterior. O e-mail de login
 * aparece antes e depois de criar a senha: quem recebe o convite num
 * endereço com "+algo" (ou num apelido da caixa) precisa saber exatamente
 * o que digitar no login.
 *
 * Só apresentação e validação local; `definirSenha` (do container
 * ConviteUsuario) fala com o backend e rejeita com o erro dele.
 */
import React, { useEffect, useRef, useState } from "react";

import { CampoDeSenha, MedidorDeForca, Requisitos } from "./CamposDaSenha";
import { MensagemErro, MensagemSucesso } from "./MensagemCampo";
import {
  CAMPO_CONFIRMACAO,
  CAMPO_DO_FORMULARIO_GERAL,
  CAMPO_NOVA,
  CONFERE,
  MENSAGEM_SENHAS_NAO_CONFEREM,
  NAO_CONFERE,
  conferenciaDaConfirmacao,
  erroDaSenhaNova,
} from "./regrasDaSenha";
import { IconeAlerta, IconeCheck, IconeChave, IconeEmail, IconeEntrar } from "./icones";

export const ESTADO_CARREGANDO = "carregando";
export const ESTADO_INVALIDO = "invalido";
export const ESTADO_FORMULARIO = "formulario";
export const ESTADO_CONCLUIDO = "concluido";

const PARADO = "idle";
const SALVANDO = "busy";

const ID_REQUISITOS = "convite-pw-reqs";

function EmailDeLogin({ email }) {
  return (
    <p className="a-p">
      <IconeEmail /> Seu e-mail de login: <b>{email}</b>
    </p>
  );
}

function Carregando() {
  return (
    <div className="pane mfa pwd" aria-busy="true">
      <div className="kicker">Convite</div>
      <h1 className="a-h">Conferindo o seu convite…</h1>
      <p className="a-p">Isso leva só um instante.</p>
    </div>
  );
}

function Invalido({ mensagem, onIrParaLogin }) {
  return (
    <div className="pane mfa pwd">
      <div className="mfa-i">
        <IconeAlerta />
      </div>
      <div className="kicker">Convite</div>
      <h1 className="a-h">Este link não vale mais</h1>
      <p className="a-p">
        {mensagem || "O convite expirou ou já foi usado."} Peça ao administrador da sua empresa para
        enviar um convite novo — o link vale por 24 horas e só pode ser usado uma vez.
      </p>
      <button className="e-btn" type="button" onClick={onIrParaLogin}>
        <IconeEntrar />
        Ir para o login
      </button>
    </div>
  );
}

function Concluido({ email, onIrParaLogin }) {
  return (
    <div className="pane mfa pwd">
      <div className="mfa-i">
        <IconeCheck />
      </div>
      <div className="kicker">Senha criada</div>
      <h1 className="a-h">Tudo certo!</h1>
      <p className="a-p">Agora é só entrar com o seu e-mail e a senha que você acabou de criar.</p>
      <EmailDeLogin email={email} />
      <button className="e-btn" type="button" onClick={onIrParaLogin}>
        <IconeEntrar />
        Ir para o login
      </button>
    </div>
  );
}

function Formulario({ convite, definirSenha }) {
  const [valores, setValores] = useState({ nova: "", confirmacao: "" });
  const [erros, setErros] = useState({});
  const [status, setStatus] = useState(PARADO);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const ocupado = status !== PARADO;
  const { nova, confirmacao } = valores;

  function mudar(campo) {
    return (valor) => {
      setValores((atual) => ({ ...atual, [campo]: valor }));
      setErros({});
    };
  }

  async function enviar(evento) {
    evento.preventDefault();
    if (ocupado) return;
    const erro = erroDaSenhaNova(valores);
    if (erro) {
      setErros({ [erro.campo]: erro.mensagem });
      return;
    }
    setErros({});
    setStatus(SALVANDO);
    try {
      await definirSenha(valores);
    } catch (falha) {
      if (!montado.current) return;
      setStatus(PARADO);
      setErros({ [CAMPO_DO_FORMULARIO_GERAL]: falha?.message || "Não foi possível criar a senha. Tente novamente." });
    }
  }

  const conferencia = conferenciaDaConfirmacao(nova, confirmacao);
  const erroConfirmacao = erros[CAMPO_CONFIRMACAO] || (conferencia === NAO_CONFERE ? MENSAGEM_SENHAS_NAO_CONFEREM : "");
  const descricaoNova = [ID_REQUISITOS, erros[CAMPO_NOVA] && "convite-pw-new-erro"].filter(Boolean).join(" ");
  const usuario = convite?.usuario || {};

  return (
    <div className="pane mfa pwd">
      <div className="mfa-i">
        <IconeChave />
      </div>
      <div className="kicker">{convite?.empresa ? `Convite · ${convite.empresa}` : "Convite"}</div>
      <h1 className="a-h">Crie a sua senha</h1>
      <p className="a-p">
        Olá, <b>{usuario.nome}</b>! Você recebeu um convite
        {usuario.cargo ? (
          <>
            {" "}para o cargo <b>{usuario.cargo}</b>
          </>
        ) : null}
        . Crie uma senha só sua para entrar no Pharu ERP.
      </p>
      <EmailDeLogin email={usuario.email} />

      <form onSubmit={enviar} noValidate>
        <CampoDeSenha
          id="convite-pw-new"
          rotulo="Nova senha"
          autoComplete="new-password"
          placeholder="Mínimo de 8 caracteres"
          nomeDoOlho="nova senha"
          valor={nova}
          aoMudar={mudar("nova")}
          invalido={Boolean(erros[CAMPO_NOVA])}
          descritoPor={descricaoNova}
          somenteLeitura={ocupado}
          autoFocus
        >
          <MedidorDeForca senha={nova} />
          <Requisitos id={ID_REQUISITOS} nova={nova} />
          {erros[CAMPO_NOVA] && <MensagemErro id="convite-pw-new-erro">{erros[CAMPO_NOVA]}</MensagemErro>}
        </CampoDeSenha>

        <CampoDeSenha
          id="convite-pw-conf"
          rotulo="Confirmar nova senha"
          autoComplete="new-password"
          placeholder="Repita a nova senha"
          nomeDoOlho="confirmação"
          valor={confirmacao}
          aoMudar={mudar("confirmacao")}
          invalido={Boolean(erros[CAMPO_CONFIRMACAO])}
          descritoPor={erroConfirmacao && "convite-pw-conf-erro"}
          somenteLeitura={ocupado}
        >
          {erroConfirmacao && <MensagemErro id="convite-pw-conf-erro">{erroConfirmacao}</MensagemErro>}
          {!erroConfirmacao && conferencia === CONFERE && <MensagemSucesso>As senhas conferem.</MensagemSucesso>}
        </CampoDeSenha>

        {erros[CAMPO_DO_FORMULARIO_GERAL] && (
          <MensagemErro className="srv">{erros[CAMPO_DO_FORMULARIO_GERAL]}</MensagemErro>
        )}
        <button
          className={`e-btn${status === SALVANDO ? " busy" : ""}`}
          type="submit"
          aria-busy={status === SALVANDO || undefined}
        >
          {status === SALVANDO ? (
            <>
              <span className="spin" aria-hidden="true" />
              Salvando…
            </>
          ) : (
            <>
              <IconeChave />
              Criar senha
            </>
          )}
        </button>
      </form>

      <p className="alt">
        Dica: uma frase curta com números e um símbolo é forte e fácil de lembrar. Não use o nome da empresa nem
        senhas de outros sistemas.
      </p>
    </div>
  );
}

export default function PainelConvite({ estado, convite, mensagemDeErro, definirSenha, onIrParaLogin }) {
  if (estado === ESTADO_CARREGANDO) return <Carregando />;
  if (estado === ESTADO_INVALIDO) return <Invalido mensagem={mensagemDeErro} onIrParaLogin={onIrParaLogin} />;
  if (estado === ESTADO_CONCLUIDO) return <Concluido email={convite?.usuario?.email} onIrParaLogin={onIrParaLogin} />;
  return <Formulario convite={convite} definirSenha={definirSenha} />;
}
